import { hapticPatterns, type FeedbackRequest } from './feedbackTypes';

type WebActuator = {
  playEffect?: (type: string, options: Record<string, number>) => Promise<unknown> | unknown;
  pulse?: (intensity: number, duration: number) => Promise<unknown> | unknown;
};

let audioContext: AudioContext | null = null;
let audioResume: Promise<void> | null = null;
const pendingCues: Array<{ cue: FeedbackRequest['cue']; volumeScale: number }> = [];
let lastBoundaryAt = 0;
let lastAudioCue: FeedbackRequest['cue'] | null = null;
let lastAudioAt = 0;
let lastHapticStatus = 'not-tested';

const soundRecipes = {
  'focus-item': [[620, 0.070, 0.320], [930, 0.115, 0.320]],
  'focus-group': [[520, 0.050, 0.300], [760, 0.075, 0.340]],
  activate: [[620, 0.070, 0.286], [930, 0.115, 0.286]],
  back: [[520, 0.055, 0.080], [360, 0.110, 0.080]],
  boundary: [[180, 0.065, 0.080]],
  success: [[660, 0.055, 0.080], [990, 0.120, 0.080]],
  error: [[220, 0.070, 0.080], [170, 0.140, 0.080]],
} as const;

const playerCueVolume = 0.5;

function scheduleSound(cue: FeedbackRequest['cue'], volumeScale = 1) {
  if (!audioContext || audioContext.state !== 'running') return;
  const context = audioContext;
  const now = context.currentTime;
  for (const [frequency, end, volume] of soundRecipes[cue]) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = cue === 'boundary' || cue === 'error' ? 'triangle' : 'sine';
    oscillator.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume * volumeScale, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + end);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + end + 0.008);
  }
}

function getAudioContext() {
  const Context = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Context) return null;
  audioContext ??= new Context();
  return audioContext;
}

function resumeAudioContext() {
  const context = getAudioContext();
  if (!context || context.state !== 'suspended') return Promise.resolve();
  if (!audioResume) {
    audioResume = context.resume()
      .catch(() => undefined)
      .finally(() => { audioResume = null; });
    audioResume.then(() => {
      if (context.state !== 'running') return;
      while (pendingCues.length) {
        const pending = pendingCues.shift()!;
        scheduleSound(pending.cue, pending.volumeScale);
      }
    });
  }
  return audioResume;
}

export function unlockTVFeedbackAudio(userActivated = true) {
  const context = getAudioContext();
  // Creating the context is safe during mount. Resuming it is deliberately
  // deferred until a real pointer/key gesture, otherwise autoplay rejection
  // can make every later navigation cue silent in some browsers.
  if (userActivated && context?.state === 'suspended') void resumeAudioContext();
}

function playSound(cue: FeedbackRequest['cue'], volumeScale = 1) {
  const context = getAudioContext();
  if (!context) return;
  if (context.state === 'suspended') {
    pendingCues.push({ cue, volumeScale });
    if (pendingCues.length > 12) pendingCues.splice(0, pendingCues.length - 12);
    void resumeAudioContext();
  } else {
    scheduleSound(cue, volumeScale);
  }
}

function playControllerHaptic(cue: FeedbackRequest['cue']) {
  const gamepad = Array.from(navigator.getGamepads?.() ?? []).find((candidate): candidate is Gamepad => Boolean(candidate?.connected));
  if (!gamepad) return;
  const candidate = gamepad as Gamepad & { vibrationActuator?: WebActuator; hapticActuators?: WebActuator[] };
  const actuator = candidate.vibrationActuator ?? candidate.hapticActuators?.[0];
  if (!actuator) {
    const vibrate = navigator.vibrate?.bind(navigator);
    if (vibrate) {
      lastHapticStatus = 'navigator-vibrate-requested';
      vibrate(hapticPatterns[cue].duration);
    } else lastHapticStatus = 'controller-connected-no-actuator';
    return;
  }
  const pattern = hapticPatterns[cue];
  if (actuator.playEffect) {
    lastHapticStatus = 'dual-rumble-requested';
    try {
      void Promise.resolve(actuator.playEffect('dual-rumble', { startDelay: 0, duration: pattern.duration, weakMagnitude: pattern.weak, strongMagnitude: pattern.strong }))
        .then(() => { lastHapticStatus = 'dual-rumble-complete'; })
        .catch(() => { lastHapticStatus = 'dual-rumble-rejected'; });
    } catch {
      lastHapticStatus = 'dual-rumble-threw';
    }
  } else if (actuator.pulse) {
    lastHapticStatus = 'pulse-requested';
    void Promise.resolve(actuator.pulse(Math.max(pattern.weak, pattern.strong), pattern.duration))
      .then(() => { lastHapticStatus = 'pulse-complete'; })
      .catch(() => { lastHapticStatus = 'pulse-rejected'; });
  }
}

function hasConnectedGamepad() {
  return Array.from(navigator.getGamepads?.() ?? []).some((candidate) => Boolean(candidate?.connected));
}

export function performTVFeedback(request: FeedbackRequest) {
  if (request.cue === 'boundary') {
    const now = performance.now();
    if (now - lastBoundaryAt < 250) return;
    lastBoundaryAt = now;
  }
  if (request.settings.uiSounds) {
    const now = performance.now();
    // Activating a card immediately focuses its Play action. That focus
    // transition is a visual handoff, not a second user action; playing both
    // near-identical recipes at once creates an unnecessarily loud burst.
    const handoff = request.cue === 'focus-item' && lastAudioCue === 'activate' && now - lastAudioAt < 140;
    if (!handoff) {
      playSound(request.cue, request.route === 'Player' ? playerCueVolume : 1);
      lastAudioCue = request.cue;
      lastAudioAt = now;
    }
  }
  const controllerInput = request.modality === 'gamepad' || (request.modality === 'keyboard' && hasConnectedGamepad());
  if (request.settings.controllerHaptics && controllerInput) playControllerHaptic(request.cue);
  if (typeof globalThis !== 'undefined') {
    (globalThis as typeof globalThis & { __JELLYFLIX_TV_FEEDBACK__?: unknown }).__JELLYFLIX_TV_FEEDBACK__ = {
      cue: request.cue,
      route: request.route,
      modality: request.modality,
      audio: request.settings.uiSounds ? (audioContext?.state ?? 'requested') : 'disabled',
      haptics: request.settings.controllerHaptics && controllerInput ? lastHapticStatus : 'disabled',
    };
  }
}
